<template>
  <v-main class="pt-0 pl-5 pr-2">
    <v-row>
      <v-col cols="12">
        <v-row>
          <v-col cols="12" md="6">
            <v-row>
              <v-col cols="12" class="font-size--16 mb-4">
                <h4 class="mb-2 font-weight-bold">
                  {{ $t('L_your_current_withdrawal_date') }}
                </h4>
                <div class="font-size--24 font-weight-regular">
                  {{ getCurrentWithDrawalDate }}
                </div>
              </v-col>
              <v-col cols="12" mt-2>
                <h4 class="font-weight-bold mb-2">
                  {{ $t('L_select_a_new_withdrawal_day') }}
                </h4>
                <div class="font-size--24 font-weight-regular mr-1">
                  <OnexSelect
                    :items="getDateOptions"
                    v-model="selectedOption"
                    :prepend-icon="mdiCalendarRange"
                    class="selectMaxWidth"
                    hideLabel
                    :label="$t('L_Withdrawal_date')"
                    :menu-props="{ location: 'bottom center', scrollStrategy: 'close' }"
                  />
                </div>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <v-row
      class="align-center pa-2 mr-1"
      :class="
        selectDateIssueNextPayDateEarly === 1 || selectDateIssueNextPayDateEarly === 3
          ? 'warning-box'
          : 'info-box'
      "
      v-if="selectDateIssueNextPayDateEarly !== -1"
    >
      <v-col cols="12">
        <v-row>
          <v-col
            cols="12"
            v-if="selectDateIssueNextPayDateEarly === 1 || selectDateIssueNextPayDateEarly === 3"
          >
            <span class="pr-2 mb-4">
              <v-icon :color="'#f39c12'" size="large" class="font-size--28" :icon="mdiAlert" />
            </span>
            <span class="font-size--14" v-if="selectDateIssueNextPayDateEarly === 3"
              >{{ $t('L_because_your_current_draft_day_is_within_the_next') }}
            </span>
            <span class="font-size--14" v-if="selectDateIssueNextPayDateEarly === 1"
              >{{ $t('L_because_you_have_chosen_a_date_in_the_past') }}
            </span>
          </v-col>
          <v-col
            cols="12"
            v-if="
              selectDateIssueNextPayDateEarly === 1 ||
              selectDateIssueNextPayDateEarly === 2 ||
              selectDateIssueNextPayDateEarly === 3
            "
          >
            <div class="font-size--14 mt-4 mb-4">
              {{
                $t('L_going_forward_your_next_payment_will_be_withdrawn_on_the', {
                  dayth: getSelectedDate,
                })
              }}
            </div>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <!-- Button -->
    <v-row class="d-flex align-center justify-end py-4">
      <v-col cols="12" sm="6" md="4" class="pr-4">
        <OnexButton
          block
          variant="outlined"
          color="primary"
          @click="cancelWithDrawalDate"
          class="maxwidthButton"
        >
          {{ $t('L_cancel') }}
        </OnexButton>
      </v-col>
      <v-col cols="12" sm="6" md="4" class="pr-4">
        <OnexButton
          block
          class="maxwidthButton"
          @click="goToNextStep"
          :disabled="disableContinueButton"
          color="primary"
        >
          {{ $t('L_continue') }}
        </OnexButton>
      </v-col>
    </v-row>
  </v-main>
</template>

<script lang="ts">
import { usePolicyStore } from '@/modules/policy/store/policy.store';
import { formatDate } from '@/shared/util/formatters';
import { useCustomerStore } from '@/stores/customer/customer.store';
import { useGlobalStore } from '@/stores/global/global.store';
import { mdiAlert, mdiCalendarRange } from '@mdi/js';
import { addMonths, format, parse } from 'date-fns';
import { mapState } from 'pinia';
import { defineComponent, type ComponentOptions } from 'vue';
import { OnexButton, OnexSelect } from '@primerica/onex-ui-lib';

const componentOptions: ComponentOptions = {
  name: 'WDStepOne',
  components: {
    OnexButton,
    OnexSelect,
  },
  data: () => ({
    // text: undefined,
    // progressLoading: false,
    // documentContent: undefined,
    selectedOption: undefined,
    persistent: true,
    hideMinimum: false,
    mdiAlert,
    mdiCalendarRange,
  }),
  computed: {
    ...mapState(useGlobalStore, {
      dateFormat: 'getDateFormat',
      locale: 'getLocale',
    }),
    ...mapState(useCustomerStore, ['customerProfile']),
    ...mapState(usePolicyStore, [
      'policies',
      'currentPolicy',
      'routeNameToNavigateBack',
      'selectedPolicyIndex',
    ]),
    disableContinueButton() {
      return this.selectedOption === undefined || this.selectedOption === '';
    },
    selectDateIssueNextPayDateEarly() {
      let issueWithDate;
      if (this.selectedOption !== '') {
        try {
          const i = this.$dynatrace.enterAction(
            `SelectWithdrawalDay_${this.selectedOption}_Selected`
          );
          this.$dynatrace.leaveAction(i);
        } catch (_error) {
          /* empty */
        }
      }
      // Current Day and Current Month (remember index 0 is Jan)
      const currentMonthDay = new Date().getDate();
      const currentMonth = new Date().getMonth();
      // New Draft Day selected by User
      const newDraftDay = this.selectedOption;
      // Current Draft Day (aka Existing Draft Day)
      // this.dateFormat
      const currentDraftDate = formatDate(this.currentPolicy?.nextDraftDate, 'MM/dd/yyyy');
      // new Date() parses 'dd/MM/yyyy' strings as MM/dd/yyyy, so parse explicitly with the matching format
      const currentDraftDateDay = parse(currentDraftDate, 'MM/dd/yyyy', new Date()).getDate();
      // const currentDraftDateMonth = moment(currentDraftDate).month();
      // Payment Schedule
      const nextPaymentDueDate = formatDate(this.currentPolicy?.nextPaymentDueDate, 'MM/dd/yyyy');
      // const nextPaymentDueDay = moment(nextPaymentDueDate).date();
      const nextPaymentDueMonth = new Date(nextPaymentDueDate).getMonth(); // 0 is Jan, 11 is Dec
      // MYP-8254 and MYP-8268 for logic
      // Have not selected date
      if (!newDraftDay || newDraftDay < 0) {
        issueWithDate = -1;
      } else if (newDraftDay < currentMonthDay && nextPaymentDueMonth > currentMonth) {
        // Payment has been made for current month
        issueWithDate = 2;
        // Going forward your payments will be withdrawn
      } else if (newDraftDay < currentMonthDay && nextPaymentDueMonth <= currentMonth) {
        // Payment has not been made for current month
        issueWithDate = 1;
        // Because you have chosen a date in the past && Going forward your payments will be withdrawn
      } else if (newDraftDay > currentDraftDateDay && newDraftDay - 3 > currentMonthDay) {
        // new draft after current draft date.  Current Draft date more than 3 days from current date
        issueWithDate = 2;
        // Going forward your payments will be withdrawn
      } else if (newDraftDay > currentDraftDateDay && newDraftDay - 3 < currentMonthDay) {
        // new draft after current draft date.  Current Draft date less than or equal to 3 days from current date
        issueWithDate = 3;
        // Because your current draft day is within the next 3 days, && Going forward your payments will be withdrawn
      } else if (newDraftDay < currentDraftDateDay && newDraftDay - 3 > currentMonthDay) {
        // new draft before current draft date.  Current Draft date more than 3 days from current date
        issueWithDate = 2;
        // Going forward your payments will be withdrawn
      } else if (newDraftDay < currentDraftDateDay && newDraftDay - 3 < currentMonthDay) {
        // new draft before current draft date. Current Draft date less than or equal to 3 days from current date
        issueWithDate = 3;
        // Because your current draft day is within the next 3 days, && Going forward your payments will be withdrawn
      } else {
        issueWithDate = 2; // Going forward your payments will be withdrawn
      }
      // if (issueWithDate > 0 && currentDraftDateMonth > currentMonth) {
      //   issueWithDate = 2;
      // }
      return issueWithDate;
    },
    getDateForNextPayment() {
      let tDate = new Date();
      tDate.setDate(this.selectedOption); // 'MM/DD/YYYY';
      const now = new Date();
      if (tDate <= now) {
        tDate = new Date();
        tDate.setDate(this.selectedOption);
        tDate = addMonths(tDate, 1);
      }
      return format(tDate, 'MM/dd/yyyy');
    },
    getCurrentWithDrawalDate() {
      // return formatDate(this.currentPolicy?.nextPaymentDueDate, 'll');
      if (this.locale === 'fr_CA') {
        return formatDate(this.currentPolicy?.nextDraftDate, 'dd/MM/yyyy');
      }
      return formatDate(this.currentPolicy?.nextDraftDate, 'MM/dd/yyyy');
    },
    getDateOptions() {
      const dateOptions = [];
      const currentDate = formatDate(this.currentPolicy?.nextDraftDate, 'MM/dd/yyyy');
      let day = -1;
      if (currentDate.length > 6) {
        day = parseInt(currentDate.substr(3, 2), 10);
      }
      for (let i = 1; i < 29; i++) {
        if (i !== day) {
          if (i === 1) {
            dateOptions.push({ value: i.toString(), title: `${i}st` });
          } else if (i === 2) {
            dateOptions.push({ value: i.toString(), title: `${i}nd` });
          } else if (i === 3) {
            dateOptions.push({ value: i.toString(), title: `${i}rd` });
          } else if (i === 21) {
            dateOptions.push({ value: i.toString(), title: `${i}st` });
          } else if (i === 22) {
            dateOptions.push({ value: i.toString(), title: `${i}nd` });
          } else if (i === 23) {
            dateOptions.push({ value: i.toString(), title: `${i}rd` });
          } else {
            dateOptions.push({ value: i.toString(), title: `${i}th` });
          }
        }
      }

      return dateOptions;
    },
    getSelectedDate() {
      let x;
      x = this.selectedOption + '';
      if (this.selectedOption === '1' || this.selectedOption === '21') {
        x += 'st ';
      } else if (this.selectedOption === '2' || this.selectedOption === '22') {
        x += 'nd ';
      } else if (this.selectedOption === '3' || this.selectedOption === '23') {
        x += 'rd ';
      } else {
        x += 'th ';
      }
      return x;
    },
  },
  methods: {
    cancelWithDrawalDate() {
      this.$emit('cancelDialog');
      try {
        const i = this.$dynatrace.enterAction('SelectWithdrawalDay_Cancel_Selected');
        this.$dynatrace.leaveAction(i);
      } catch (_error) {
        /* empty */
      }
    },
    async goToNextStep() {
      try {
        const i = this.$dynatrace.enterAction('SelectWithdrawalDay_Continue_Selected');
        await this.$dynatrace.leaveAction(i);
      } catch (_error) {
        /* empty */
      }
      try {
        const i = this.$dynatrace.enterAction('ReviewWithdrawalDay_PageDisplayed');
        await this.$dynatrace.leaveAction(i);
      } catch (_error) {
        /* empty */
      }
      this.$emit('goToWDStepTwo', this.selectedOption);
      this.$emit('selectDateIssueNextPayDateEarly', this.selectDateIssueNextPayDateEarly);
      this.$emit('setDateForNextPayment', this.getDateForNextPayment);
      // Setting data back to default;
      this.selectedOption = '';
    },
  },
};

export default defineComponent(componentOptions);
</script>

<style lang="scss" scoped>
.selectMaxWidth {
  max-width: 320px;
  width: 100%;
  padding-bottom: 4px !important;
}

.bg-light-grey {
  background-color: #f5f5f5;
}

.cardNotSelected {
  max-width: 295px;
  border: 2px solid #cccccc;
  background-color: #ffffff;
  height: 85px;
}

.info-box {
  background-color: #e2f0fe !important;
  border-top: 4px solid #3399ff;
  margin-left: 0px !important;
  border-left: 0px;
  border-right: 0px;
  border-bottom: 0px;
  padding: 2px 10px 4px 10px;

  a {
    font-size: 16px;
  }
}

.warning-box {
  background-color: #fdf3e2 !important;
  border-top: 4px solid #f39c12;
  margin-left: 0px !important;
  border-left: 0px;
  border-right: 0px;
  border-bottom: 0px;
  padding: 2px 10px 4px 10px;

  a {
    font-size: 16px;
  }
}

.cardSelected {
  max-width: 295px;
  border: 2px solid #6cd07b;
  background-color: #d8f8dd;
  height: 85px;
}

.cardTitle {
  font-size: 16px;
  color: #333333;
}

.cardAmount {
  padding-top: 0px;
  padding-left: 16px;
  font-size: 24px;
  font-weight: bold;
  color: #333333;
}

.cardFooter {
  padding-top: 8px;
  font-size: 12px;
  color: #333333;
}

.vertCenterCircleHoriRight {
  margin: auto;
  text-align: right;
  padding-right: 10px;
}

.firstTextContent {
  font-size: 16px;
  font-weight: 600;
  color: #333333;
}

.secondTextContent {
  font-size: 16px;
  font-weight: 400;
  color: #333333;
}

.thirdTextContent {
  font-size: 16px;
  font-weight: 600;
  color: #333333;
}

.maxwidthButton {
  max-width: 300px;
}

.moveRight {
  text-align: right;
}

.clickable {
  cursor: pointer;
}
</style>
